import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { forwardRef, useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';

import type { Assignment } from './assignments-section.types';

const logger = getLogger('techline');

/** Inline style for left-padding the assignment label and empty placeholder text. */
const labelOffsetStyle = { marginLeft: 8 };

/** Inline style for the checkmark icon spacing inside the "Mark as done" button. */
const checkmarkIconStyle = { marginRight: 8 };

/** Inline style for the card body content area. */
const cardBodyStyle = { padding: 16, paddingRight: 8 };

/** Inline style to remove the bottom margin from the details list. */
const detailsListStyle = { marginBottom: 0 };

/**
 * Props for AssignmentCard.
 *
 * Presentational component that displays a single technician assignment
 * and allows toggling its completion status via a callback.
 */
export type AssignmentCardProps = {
    /** The assignment data to display. When undefined, an empty placeholder card is shown. */
    assignment: Assignment | undefined;

    /** Index used for carousel scroll targeting via `data-index` attribute. */
    dataIndex?: number;

    /** Whether the action button in the header should be displayed. */
    showActionButton?: boolean;

    /** Whether the case is closed, disabling completion toggles. */
    isCaseClosed?: boolean;

    /**
     * Callback to toggle the completion status of this assignment.
     *
     * @param assignmentId - the ID of the assignment to toggle
     */
    onToggleCompletion?: (assignmentId: string) => Promise<void>;
};

/**
 * Displays a single technician assignment card with a header showing the
 * assignment label and a toggle button, and a body listing detail items.
 * When no assignment is provided, renders an empty placeholder card.
 *
 * This is a presentational component; completion toggling is delegated
 * to the parent via the `onToggleCompletion` callback.
 *
 * @param props - {@link AssignmentCardProps}
 * @param ref - forwarded ref to the root div element
 * @returns the assignment card JSX
 */
export const AssignmentCard = forwardRef<HTMLDivElement, AssignmentCardProps>(
    ({ assignment, dataIndex, showActionButton = true, isCaseClosed = false, onToggleCompletion }, ref) => {
        // State
        const [isToggling, setIsToggling] = useState(false);

        // Hooks
        const classes = useStyles();
        // Handlers

        /**
         * Handle the completion toggle button press by delegating to the
         * parent callback with the assignment ID. Disables the button while
         * the async operation is in progress to prevent double-clicks.
         */
        const handleToggleButtonPress = useCallback(async () => {
            if (!assignment?.caseTechnicianAssignmentId) {
                logger.error('Failed to toggle assignment completion because no caseTechnicianAssignmentId was found');
                return;
            }

            setIsToggling(true);
            try {
                await onToggleCompletion?.(assignment.caseTechnicianAssignmentId);
            } finally {
                setIsToggling(false);
            }
        }, [assignment?.caseTechnicianAssignmentId, onToggleCompletion]);

        return (
            <div className={classes.assignmentCard} data-index={dataIndex} ref={ref}>
                <div className={classes.assignmentHeader}>
                    <div className="d-flex align-items-center">
                        {assignment && assignment.completed && <Icon color="greenOne" name="check-circle-hollow" />}
                        {assignment && (
                            <Typography style={labelOffsetStyle} variant="label">
                                {assignment.label}
                            </Typography>
                        )}

                        {!assignment && (
                            <Typography color="graySix" style={labelOffsetStyle} variant="label">
                                No assignments from Techline
                            </Typography>
                        )}
                    </div>
                    {assignment && showActionButton && (
                        <Button
                            disabled={isToggling || isCaseClosed}
                            onPress={handleToggleButtonPress}
                            variant={assignment.completed ? 'link' : 'primary'}
                        >
                            {assignment.completed === false && (
                                <Icon color="white" name="checkmark" size={20} style={checkmarkIconStyle} />
                            )}
                            <Typography color={assignment.completed ? 'blueOne' : 'white'}>
                                {assignment.completed ? 'Mark as not done' : 'Mark as done'}
                            </Typography>
                        </Button>
                    )}
                </div>
                <div style={cardBodyStyle}>
                    <div className={assignment ? classes.assignmentBody : classes.emptyAssignmentBody}>
                        {assignment && (
                            <ul style={detailsListStyle}>
                                {assignment.details.length > 0 &&
                                    assignment.details.map((detailText, index) => (
                                        <li key={index}>
                                            <Typography>{detailText}</Typography>
                                        </li>
                                    ))}
                            </ul>
                        )}
                        {!assignment && (
                            <Typography color="grayFive" variant="default">
                                Any actions assigned by Techline Reps will appear here
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

const useStyles = createUseStyles({
    assignmentCard: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        backgroundColor: colors.white,
        marginTop: 6,
        width: 500,
        minWidth: 500,
        maxHeight: 208,
        height: 208,
    },
    assignmentHeader: {
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 16px',
        height: 54,
    },
    assignmentBody: {
        maxHeight: 120,
        overflowY: 'hidden',
        transition: 'overflow-y 0.2s ease-in-out',
        '&:hover': {
            overflowY: 'auto',
        },
        '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
            marginRight: '10px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-track-piece': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: colors.grayFour,
            borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: colors.grayFive,
        },
        '&::-webkit-scrollbar-button': {
            display: 'none',
        },
        '&::-webkit-scrollbar-corner': {
            display: 'none',
        },
    },
    emptyAssignmentBody: {
        minHeight: '90px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
});
